/**
 * Tenant persistence utilities
 *
 * Manages tenant storage and URL path construction for multi-tenant support.
 * Tenants are stored in localStorage to persist across sessions.
 */

const TENANT_STORAGE_KEY = 'lastUsedTenant';

/**
 * Extract tenant reference from URL pathname
 * @example extractTenantFromPath('/t/rds-test/login') => 'rds-test'
 * @example extractTenantFromPath('/login') => null
 */
export function extractTenantFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/t\/([^/]+)/);
  return match ? match[1] : null;
}

/**
 * Get the last-used tenant from localStorage
 */
export function getStoredTenant(): string | null {
  try {
    return localStorage.getItem(TENANT_STORAGE_KEY);
  } catch (error) {
    console.warn('[tenantUtils] Failed to read from localStorage:', error);
    return null;
  }
}

/**
 * Store tenant reference in localStorage
 */
export function setStoredTenant(tenant: string): void {
  try {
    localStorage.setItem(TENANT_STORAGE_KEY, tenant);
  } catch (error) {
    console.warn('[tenantUtils] Failed to write to localStorage:', error);
  }
}

/**
 * Clear stored tenant from localStorage
 */
export function clearStoredTenant(): void {
  try {
    localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch (error) {
    console.warn('[tenantUtils] Failed to clear localStorage:', error);
  }
}

/**
 * Build a tenant-scoped path
 * @example buildTenantPath('rds-test', '/login') => '/t/rds-test/login'
 * @example buildTenantPath('rds-test', '/dashboard') => '/t/rds-test/dashboard'
 */
export function buildTenantPath(tenant: string, path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `/t/${tenant}${normalizedPath}`;
}

/**
 * Check if current URL has tenant in path
 */
export function hasTenantInPath(pathname: string): boolean {
  return /^\/t\/[^/]+/.test(pathname);
}
